import { useState } from 'react';
import { Box, Button, Tooltip, useTheme } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from '@/utils/dateFormatter';
import { getErrorMessage } from '@/utils/errorMessage';
import { appraisalDetailKeys } from '@/queries/useAppraisalDetail';
import { appraisalDetailApi } from '@/api/appraisalDetailApi';
import type { RecordAccess } from '@/auth/permissions.types';
import type {
  AppraisalDetailResponse,
  InterviewSummaryEntry,
} from '@/api/types/appraisal.types';
import {
  validateForHandover,
  validateForSubmitToTc,
  hasErrors,
  NO_ERRORS,
} from './appraisalValidation';
import type { AppraisalValidationErrors } from './appraisalValidation';
import { HandoverToCollatorDialog } from './HandoverToCollatorDialog';
import { ConfirmDeliveryDialog } from './ConfirmDeliveryDialog';
import { ReturnToAppraiseeDialog } from './ReturnToAppraiseeDialog';
import { SubmitToTalentCommitteeDialog } from './SubmitToTalentCommitteeDialog';
import { DeliverToAppraiseeDialog } from './DeliverToAppraiseeDialog';

interface AppraisalFooterProps {
  appraisalId: string;
  permissions: RecordAccess | undefined;
  appraisalDetail: AppraisalDetailResponse;
  interviewSummaryEntries: InterviewSummaryEntry[];
  setSnackbar: (snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }) => void;
  setValidationErrors: (errors: AppraisalValidationErrors, source: 'handover' | 'submitToTc' | null) => void;
  onDownloadPdf?: () => void;
  isGeneratingPdf?: boolean;
}

export function AppraisalFooter({
  appraisalId,
  permissions,
  appraisalDetail,
  interviewSummaryEntries,
  setSnackbar,
  setValidationErrors,
  onDownloadPdf,
  isGeneratingPdf = false,
}: AppraisalFooterProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();

  // Dialog state
  const [handoverDialogOpen, setHandoverDialogOpen] = useState(false);
  const [confirmDeliveryDialogOpen, setConfirmDeliveryDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [submitToTcDialogOpen, setSubmitToTcDialogOpen] = useState(false);
  const [deliverDialogOpen, setDeliverDialogOpen] = useState(false);

  // Extract action permissions
  const canSave = permissions?.actions.canSave || false;
  const canDownload = permissions?.actions.canDownload || false;
  const canSubmitSelfAssessmentToCollator =
    permissions?.actions.canSubmitSelfAssessmentToCollator || false;
  const canConfirmDelivery = permissions?.actions.canConfirmDelivery || false;
  const canReturnToAppraisee = permissions?.actions.canReturnToAppraisee || false;
  const canSubmitToTalentCommittee =
    permissions?.actions.canSubmitToTalentCommittee || false;
  const canDeliverToAppraisee = permissions?.actions.canDeliverToAppraisee || false;

  // Post-delivery disabled button: only Collator/Delegate keep a visible (disabled) button
  // after the deliver action is gone, AND only while the appraisal is in a post-delivery
  // status. The DB columns deliveredAt / scheduledDeliveryDate persist through later
  // states (e.g. status edited back to CollatorReview), so the status gate is what
  // actually scopes the button to the delivery-complete window.
  const isCollatorOrDelegate =
    permissions?.role === 'Collator' || permissions?.role === 'Delegate';
  const isPostDeliveryStatus =
    appraisalDetail.appraisalStatus === 'Delivered' ||
    appraisalDetail.appraisalStatus === 'DeliveryAcknowledged';
  const showDeliveredDisabledButton =
    isCollatorOrDelegate &&
    !canDeliverToAppraisee &&
    isPostDeliveryStatus &&
    (appraisalDetail.deliveredAt != null || appraisalDetail.scheduledDeliveryDate != null);

  const hasAnyAction =
    canSave ||
    canDownload ||
    canSubmitSelfAssessmentToCollator ||
    canConfirmDelivery ||
    canReturnToAppraisee ||
    canSubmitToTalentCommittee ||
    canDeliverToAppraisee ||
    showDeliveredDisabledButton;

  if (!hasAnyAction) return null;

  // -- Handlers --

  // Appraisal sections auto-save on blur (via onBlur handlers in each editable section).
  // The Save button forces a blur on the active element, which triggers the pending auto-save,
  // then shows a "Saved" snackbar as user feedback. No explicit save API call needed.
  const handleSave = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setSnackbar({ open: true, message: 'Saved', severity: 'success' });
  };

  const handleHandoverClick = async () => {
    // SYNCHRONOUS — must run before any await so onBlur auto-saves can flush
    // before we refetch and validate.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Best-effort client-side preflight: fetch fresh detail and run the same
    // validation rules the server enforces. Server-side checks remain the
    // source of truth on submit. fetchQuery (over refetchQueries +
    // getQueryData) returns fresh data even when no useQuery observer is
    // mounted for the detail key.
    const fresh = await queryClient.fetchQuery<AppraisalDetailResponse>({
      queryKey: appraisalDetailKeys.detail(appraisalId),
      queryFn: () => appraisalDetailApi.getAppraisalDetail(Number(appraisalId)),
    });
    const errors = validateForHandover(fresh ?? appraisalDetail);
    if (hasErrors(errors)) {
      setValidationErrors(errors, 'handover');
      return;
    }
    setValidationErrors(NO_ERRORS, null);
    setHandoverDialogOpen(true);
  };

  const handleSubmitToTcClick = async () => {
    // SYNCHRONOUS — must run before any await so onBlur auto-saves can flush
    // before we refetch and validate.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Best-effort client-side preflight: fetch fresh detail + summary, then run the
    // same validation rules the server enforces. Server-side checks are still the
    // source of truth on submit. fetchQuery is preferred over refetchQueries +
    // getQueryData so we still get fresh data when the relevant useQuery has no
    // active observers (e.g., the user navigated to the footer faster than the
    // dependent component rendered, leaving the cache empty).
    const numericId = Number(appraisalId);
    const [freshDetail, freshEntries] = await Promise.all([
      queryClient.fetchQuery<AppraisalDetailResponse>({
        queryKey: appraisalDetailKeys.detail(appraisalId),
        queryFn: () => appraisalDetailApi.getAppraisalDetail(numericId),
      }),
      queryClient.fetchQuery<InterviewSummaryEntry[]>({
        queryKey: appraisalDetailKeys.interviewSummary(numericId),
        queryFn: () => appraisalDetailApi.getInterviewSummary(numericId),
      }),
    ]);
    const errors = validateForSubmitToTc(
      freshDetail ?? appraisalDetail,
      freshEntries ?? interviewSummaryEntries,
    );
    if (hasErrors(errors)) {
      setValidationErrors(errors, 'submitToTc');
      return;
    }
    setValidationErrors(NO_ERRORS, null);
    setSubmitToTcDialogOpen(true);
  };

  // -- Shared dialog callbacks --

  const handleError = (error: unknown) => {
    setSnackbar({
      open: true,
      message: getErrorMessage(error),
      severity: 'error',
    });
  };

  const handleHandoverSuccess = () => {
    setHandoverDialogOpen(false);
    setValidationErrors(NO_ERRORS, null);
    setSnackbar({
      open: true,
      message: 'Appraisal has been handed over to the Collator',
      severity: 'success',
    });
  };

  const handleConfirmDeliverySuccess = () => {
    setConfirmDeliveryDialogOpen(false);
    setValidationErrors(NO_ERRORS, null);
    setSnackbar({
      open: true,
      message: 'Delivery has been confirmed',
      severity: 'success',
    });
  };

  const handleReturnSuccess = () => {
    setReturnDialogOpen(false);
    setValidationErrors(NO_ERRORS, null);
    setSnackbar({
      open: true,
      message: 'Appraisal has been returned to the Appraisee',
      severity: 'success',
    });
  };

  const handleSubmitToTcSuccess = () => {
    setSubmitToTcDialogOpen(false);
    setValidationErrors(NO_ERRORS, null);
    setSnackbar({
      open: true,
      message: 'Appraisal has been submitted to the Talent Committee',
      severity: 'success',
    });
  };

  const handleDeliverSuccess = (wasScheduled: boolean) => {
    setDeliverDialogOpen(false);
    setValidationErrors(NO_ERRORS, null);
    setSnackbar({
      open: true,
      message: wasScheduled
        ? 'Appraisal has been scheduled to be delivered to the Appraisee'
        : 'Appraisal has been delivered to the Appraisee',
      severity: 'success',
    });
  };

  // -- Delivery tooltip text --

  const getDeliveryTooltip = (): string => {
    if (appraisalDetail.deliveredAt) {
      return `Delivered on ${formatDateTime(appraisalDetail.deliveredAt)}`;
    }
    if (appraisalDetail.scheduledDeliveryDate) {
      return `Scheduled for ${formatDateTime(appraisalDetail.scheduledDeliveryDate)}`;
    }
    return '';
  };

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: theme.palette.custom.darkTeal,
          px: 3,
          py: 1.5,
          mx: { xs: -2, md: -3 },
          mb: { xs: -2, md: -3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {canSave && (
            <Button variant="contained" color="success" onClick={handleSave}>
              Save
            </Button>
          )}
          {canDownload && (
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={onDownloadPdf}
              disabled={isGeneratingPdf}
            >
              {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {canSubmitSelfAssessmentToCollator && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleHandoverClick}
            >
              Handover to Collator
            </Button>
          )}
          {canConfirmDelivery && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setConfirmDeliveryDialogOpen(true)}
            >
              Confirm Delivery
            </Button>
          )}
          {canReturnToAppraisee && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setReturnDialogOpen(true)}
            >
              Return to Appraisee
            </Button>
          )}
          {canSubmitToTalentCommittee && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmitToTcClick}
            >
              Submit to Talent Committee
            </Button>
          )}
          {canDeliverToAppraisee && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setDeliverDialogOpen(true)}
            >
              Deliver to Appraisee
            </Button>
          )}
          {showDeliveredDisabledButton && (
            <Tooltip title={getDeliveryTooltip()}>
              <span>
                <Button variant="contained" color="secondary" disabled>
                  Deliver to Appraisee
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <HandoverToCollatorDialog
        open={handoverDialogOpen}
        onClose={() => setHandoverDialogOpen(false)}
        appraisalId={appraisalId}
        rowVersion={appraisalDetail.rowVersion}
        onSuccess={handleHandoverSuccess}
        onError={handleError}
      />
      <ConfirmDeliveryDialog
        open={confirmDeliveryDialogOpen}
        onClose={() => setConfirmDeliveryDialogOpen(false)}
        appraisalId={appraisalId}
        rowVersion={appraisalDetail.rowVersion}
        onSuccess={handleConfirmDeliverySuccess}
        onError={handleError}
      />
      <ReturnToAppraiseeDialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        appraisalId={appraisalId}
        rowVersion={appraisalDetail.rowVersion}
        onSuccess={handleReturnSuccess}
        onError={handleError}
      />
      <SubmitToTalentCommitteeDialog
        open={submitToTcDialogOpen}
        onClose={() => setSubmitToTcDialogOpen(false)}
        appraisalId={appraisalId}
        rowVersion={appraisalDetail.rowVersion}
        onSuccess={handleSubmitToTcSuccess}
        onError={handleError}
      />
      <DeliverToAppraiseeDialog
        open={deliverDialogOpen}
        onClose={() => setDeliverDialogOpen(false)}
        appraisalId={Number(appraisalId)}
        rowVersion={appraisalDetail.rowVersion}
        onSuccess={handleDeliverSuccess}
        onError={handleError}
      />
    </>
  );
}
